import { Navigate, Outlet, NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { PushBanner } from "@/components/PushBanner";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { DeviceApprovalCard } from "@/components/auth/DeviceApprovalCard";
import { PlanPill, PlanLockBanner } from "@/components/PlanStatus";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect, useRef } from "react";
import api, { imgSrc, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useEntitlements } from "@/lib/useEntitlements";
import { PlanBadge } from "@/components/Gate";
import { trackUpgrade } from "@/lib/upgradeTrack";
import { isBarnMode, initBarnMode, setBarnModePref } from "@/lib/barnMode";
import { useAuth } from "@/context/AuthContext";
import { useBarnTimezoneAutoDetect } from "@/lib/useBarnTimezone";
import { usePresence } from "@/lib/usePresence";
import {
  LayoutDashboard, Trophy, ClipboardList, Users, LogOut, Receipt, FileText, ReceiptText,
  Stethoscope, HelpCircle, MessageCircle,
  Settings as SettingsIcon, MoreHorizontal, ChevronLeft,
  Dna, ChevronDown, Lock } from "lucide-react";
import { useMessagesUnread } from "@/hooks/useMessagesUnread";
import { AssistantTutorial } from "@/components/AssistantTutorial";
import HorseIcon from "@/components/icons/HorseIcon";
import KinglyLogo from "@/components/KinglyLogo";
import { useSwipeBack } from "@/lib/useSwipeBack";
import InstallPrompt from "@/components/InstallPrompt";
import { HelpProvider } from "@/context/HelpContext";
import AskKinglySheet, { AskKinglyButton } from "@/components/help/AskKinglySheet";
import { SearchButton } from "@/components/SearchSheet";
import { useLang } from "@/i18n";
import { recordTap } from "@/lib/taps";
import GuideOverlay from "@/components/help/GuideOverlay";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

const NAV = [
  { to: "/dashboard", label: "Today", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/shows", label: "Shows", icon: Trophy, testid: "nav-shows" },
  { to: "/horses", label: "Horses", icon: HorseIcon, testid: "nav-horses",
    children: [{ to: "/vet-records", label: "Vet & meds", icon: Stethoscope, testid: "nav-vet", feature: "vet_records" },
      { to: "/breeding", label: "Breeding", icon: Dna, testid: "nav-breeding", trainerOnly: true, feature: "breeding" }] },
  { to: "/messages", label: "Chat", icon: MessageCircle, testid: "nav-messages", badge: true },
  { to: "/invoices", label: "Money", icon: Receipt, testid: "nav-money", trainerOnly: true, section: true,
    children: [
      { to: "/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices", feature: "invoicing" },
      { to: "/expenses", label: "Expenses", icon: ReceiptText, testid: "nav-expenses", feature: "expenses" },
      { to: "/books", label: "Books", icon: Receipt, testid: "nav-books", feature: "books" },
      { to: "/services", label: "Services & prices", icon: ClipboardList, testid: "nav-services" },
      { to: "/providers", label: "Providers", icon: Users, testid: "nav-providers" },
    ] },
  { to: "/services", label: "Services", icon: ClipboardList, testid: "nav-services-asst", assistantOnly: true },
  { to: "/settings", label: "Team & Settings", icon: SettingsIcon, testid: "nav-settings" },
  { to: "/help", label: "Help", icon: HelpCircle, testid: "nav-help" },
];
const MONEY_PATHS = ["/invoices", "/expenses", "/books", "/accounting", "/services", "/providers"];

// Primary phone tabs — one-thumb reach. Money, Team & Settings and Help live in "More".
const MOBILE_PRIMARY = [
  { to: "/dashboard", label: "Today", icon: LayoutDashboard, testid: "tab-dashboard", end: true },
  { to: "/shows", label: "Shows", icon: Trophy, testid: "tab-shows" },
  { to: "/horses", label: "Horses", icon: HorseIcon, testid: "tab-horses" },
  { to: "/messages", label: "Chat", icon: MessageCircle, testid: "tab-messages", badge: true },
];

/** Flat list for the phone "More" sheet: sections become a heading followed by their items. */
function flatNav(items) {
  return items.flatMap((n) => (n.section ? [{ ...n, heading: true }, ...n.children] : [n, ...(n.children || [])]));
}

/** Where "Back" lands when the app was opened straight on this page (no in-app history). */
export function backFallback(pathname) {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length <= 1) return "/dashboard";
  if (parts[0] === "shows" && parts.length >= 4) return `/shows/${parts[1]}`;
  return `/${parts[0]}`;
}

function UnreadBadge({ count, className = "" }) {
  if (!count) return null;
  return (
    <span
      className={`min-w-[18px] h-[18px] px-1 rounded-full bg-amber-400 text-slate-900 text-[10px] font-bold flex items-center justify-center ${className}`}
      data-testid="messages-unread-badge"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

/** One email, many barns: pick another linked barn → new session for that account → reload. */
function BarnSwitcher({ user, barnLabel, compact = false }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const barns = user?.barns || [];
  if (barns.length < 2) return compact ? null : <span className="text-sm font-semibold text-slate-800">{barnLabel}</span>;
  const go = async (b) => {
    if (b.current) { setOpen(false); return; }
    setBusy(b.user_id);
    try { await api.post("/auth/switch", { user_id: b.user_id }); window.location.assign("/dashboard"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(""); }
  };
  return (
    <div className="relative" data-testid="barn-switcher">
      <button type="button" onClick={() => setOpen((v) => !v)} className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-semibold hover:bg-zinc-100 ${compact ? "text-white hover:bg-white/10" : "text-slate-800"}`} data-testid="barn-switcher-btn" aria-expanded={open}>
        <span className="truncate max-w-[160px]">{barnLabel}</span><ChevronDown className="w-4 h-4 opacity-60" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 mt-1 z-[80] w-64 rounded-lg border border-zinc-200 bg-white shadow-xl py-1 text-slate-800" data-testid="barn-switcher-menu">
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500">Your barns</div>
            {barns.map((b) => (
              <button key={b.user_id} type="button" onClick={() => go(b)} disabled={!!busy} className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-zinc-50 ${b.current ? "bg-amber-50" : ""}`} data-testid={`barn-switch-${b.workspace_id}`}>
                <span className="truncate">{b.barn_name}</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-500 shrink-0">{busy === b.user_id ? "…" : b.current ? "current" : b.role_label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const TRIAL_KEY = "kingly.es-trial";
const TRIAL_SECONDS = 60;

/** EN/ES switch — one tap, saved to the user's profile. Without Pro: a 60-second try-it-out, then back to English with an upgrade nudge. */
function LangToggle() {
  const { lang, setLang, trialEnd, startTrial, stopTrial } = useLang();
  const { has } = useEntitlements();
  const navigate = useNavigate();
  const allowed = has("spanish_assistant");
  const [now, setNow] = useState(Date.now());
  const trialLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - now) / 1000)) : 0;
  const next = lang === "es" ? "en" : "es";
  useEffect(() => {
    if (!trialEnd) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [trialEnd]);
  const onClick = () => {
    if (allowed) { setLang(next); return; }
    if (lang === "es") { stopTrial(); return; }
    let used = false;
    try { used = sessionStorage.getItem(TRIAL_KEY) === "1"; } catch {}
    if (used) {
      toast.message("Spanish assistant app is part of the Pro plan", { description: "You've already had your 60-second try this session.", action: { label: "See the preview", onClick: () => { trackUpgrade("nudge_tap", "spanish_assistant", "trial_toast"); navigate("/preview/spanish_assistant?src=trial_toast"); } } });
      return;
    }
    try { sessionStorage.setItem(TRIAL_KEY, "1"); } catch {}
    trackUpgrade("trial_start", "spanish_assistant", "lang_toggle");
    startTrial(TRIAL_SECONDS, () => toast.message("That was the Spanish assistant app", { duration: 12000, description: "Your grooms tap it in Spanish; you read it in English. It's part of the Pro plan.",
      action: { label: "See the preview", onClick: () => { trackUpgrade("nudge_tap", "spanish_assistant", "trial_toast"); navigate("/preview/spanish_assistant?src=trial_toast"); } } }));
    toast.success("Spanish for 60 seconds — have a look around", { description: "Flips back to English on its own. Part of the Pro plan." });
  };
  return (
    <button
      type="button"
      data-testid="lang-toggle"
      data-no-translate
      onClick={onClick}
      title={lang === "es" ? "Switch to English" : "Cambiar a español"}
      aria-label={lang === "es" ? "Switch to English" : "Cambiar a español"}
      className={`px-2 py-1 rounded-md border font-bold leading-none text-[11px] tracking-wide transition-colors ${
        lang === "es"
          ? "bg-amber-400 border-amber-400 text-slate-900"
          : "border-slate-600 text-slate-300 md:border-zinc-300 md:text-slate-500 hover:opacity-75"
      }`}
    >
      {!allowed && lang !== "es" ? <Lock className="w-3 h-3 inline -mt-0.5 mr-0.5" /> : null}{lang === "es" ? "ES" : "EN"}
      {!allowed && lang !== "es" ? <span className="ml-1 text-[8px] text-amber-500 align-top" data-testid="lang-toggle-pro">PRO</span> : null}
      {trialLeft > 0 ? <span className="ml-1 text-[9px] tabular-nums text-slate-900/70" data-testid="lang-toggle-trial">{trialLeft}s</span> : null}
    </button>
  );
}

export default function ProtectedLayout() {
  const { user, loading, logout } = useAuth();
  const [barnMode, setBarnModeState] = useState(isBarnMode());
  useEffect(() => {
    initBarnMode();
    setBarnModeState(isBarnMode());
    const f = () => setBarnModeState(isBarnMode());
    window.addEventListener("barn-mode-change", f);
    return () => window.removeEventListener("barn-mode-change", f);
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const startIdx = useRef(window.history.state?.idx ?? 0);
  const [moreOpen, setMoreOpen] = useState(false);
  const [tutorialDismissed, setTutorialDismissed] = useState(false);

  const { data: ws } = useQuery({
    queryKey: ["workspace"],
    queryFn: async () => (await api.get("/workspace")).data,
    enabled: !!user,
  });
  const unread = useMessagesUnread(!!user && !loading && user.role !== "bookkeeper", user);
  useBarnTimezoneAutoDetect(ws, user);
  usePresence(user, location.pathname);
  const showBack = location.pathname !== "/dashboard";
  function goBack() {
    if ((window.history.state?.idx ?? 0) > startIdx.current) navigate(-1);
    else navigate(backFallback(location.pathname), { replace: true });
  }
  const pageRef = useRef(null);
  const swipeRef = useSwipeBack({ enabled: !!user && showBack, onBack: goBack, pageRef });

  if (loading || user === null) {
    return (
      <div className="flex items-center justify-center min-h-screen text-slate-500">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.workspace_type === "collegiate" || user.role === "coach" || user.role === "athlete") return <Navigate to="/collegiate/app" replace />;

  const isTrainer = user.role === "owner" || user.role === "admin";
  const isBookkeeper = user.role === "bookkeeper";
  const visibleNav = isBookkeeper
    ? [{ to: "/books", label: "Money", icon: Receipt, testid: "nav-money", section: true,
         children: [{ to: "/books", label: "Books", icon: Receipt, testid: "nav-books" },
                    { to: "/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices" }] },
       NAV.find((n) => n.to === "/help")]
    : NAV.filter((n) => (!n.trainerOnly || isTrainer) && (!n.assistantOnly || !isTrainer))
         .map((n) => (n.children ? { ...n, children: n.children.filter((c) => !c.trainerOnly || isTrainer) } : n));
  const moreNav = flatNav(visibleNav).filter((n) => !MOBILE_PRIMARY.some((p) => p.to === n.to && !n.heading));
  if (isBookkeeper && !["/books", "/accounting", "/invoices", "/help"].some((p) => location.pathname.startsWith(p))) {
    return <Navigate to="/books" replace />;
  }
  const barnLabel = ws?.name || user.barn_name || user.name;

  return (
    <HelpProvider user={user}>
    <div className="min-h-screen flex bg-zinc-50">
      {/* ===== Desktop sidebar (hidden on mobile) ===== */}
      <aside
        className="hidden md:flex w-64 border-r border-zinc-200 bg-white flex-col shrink-0"
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b border-zinc-200">
          <div className="flex items-center gap-3">
            <KinglyLogo size={44} />
            <div>
              <div className="font-display font-bold text-slate-900 leading-tight text-xl">Kingly</div>
              <div className="text-[10px] text-amber-700 uppercase tracking-[0.18em] font-semibold mt-0.5">
                Be the King of the Show
              </div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {visibleNav.map((item) => (
            <div key={item.to + item.label}>
              {item.section ? (
                <div className={`flex items-center gap-3 px-3 pt-3 pb-1 text-[11px] uppercase tracking-[0.18em] font-semibold ${MONEY_PATHS.some((p) => location.pathname.startsWith(p)) ? "text-amber-700" : "text-slate-400"}`} data-testid={item.testid}>
                  <item.icon className="w-3.5 h-3.5" /> {item.label}
                </div>
              ) : (
                <NavLink
                  to={item.to}
                  end={!!item.end}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-slate-900 text-white"
                        : "text-slate-600 hover:bg-zinc-100 hover:text-slate-900"
                    }`
                  }
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                  {item.badge && <UnreadBadge count={unread} className="ml-auto" />}
                </NavLink>
              )}
              {(item.children || []).map((sub) => (
                <NavLink
                  key={sub.to}
                  to={sub.to}
                  data-testid={sub.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 py-1.5 rounded-md text-sm transition-colors ${item.section ? "px-3" : "pl-10 pr-3"} ${
                      isActive ? "bg-slate-900 text-white font-medium" : "text-slate-500 hover:bg-zinc-100 hover:text-slate-900"
                    }`
                  }
                >
                  <sub.icon className="w-4 h-4" />
                  {sub.label}
                  {sub.feature && <PlanBadge feature={sub.feature} className="ml-auto" />}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-zinc-200">
          <UserPanel user={user} onLogout={logout} />
        </div>
      </aside>

      <main ref={swipeRef} className="flex-1 min-w-0 flex flex-col overflow-x-clip pb-[calc(env(safe-area-inset-bottom)+4.5rem)] md:pb-0" data-testid="swipe-back-area">
        {user.demo && (
          <div
            data-testid="demo-banner"
            className="bg-amber-400 text-slate-900 text-center text-xs sm:text-sm font-semibold px-4 py-2 z-40"
          >
            You&apos;re exploring the Kingly demo barn — read-only.{" "}
            <Link to="/register" className="underline underline-offset-2" data-testid="demo-banner-register-link">
              Create your own barn free
            </Link>
          </div>
        )}
        {/* ===== Header (safe-area aware for iOS notch / status bar) ===== */}
        <header
          className="bg-slate-900/95 backdrop-blur-md border-b border-slate-800
                     md:bg-white md:border-zinc-200 sticky top-0 z-30
                     pt-[env(safe-area-inset-top)]"
        >
          <div className="h-14 md:h-14 flex items-center justify-between px-4 md:px-6">
            {/* Mobile brand: logo + barn name + gold tagline */}
            <div className="md:hidden flex items-center gap-2 min-w-0" data-testid="mobile-brand">
              {showBack && (
                <button
                  type="button"
                  onClick={goBack}
                  aria-label="Back"
                  data-testid="mobile-back-btn"
                  className="-ml-2.5 w-11 h-11 shrink-0 flex items-center justify-center rounded-full text-amber-300 active:bg-white/10 active:scale-95 transition-transform"
                >
                  <ChevronLeft className="w-8 h-8 stroke-[2.5]" />
                </button>
              )}
              <KinglyLogo size={40} className="shrink-0" />
              <div className="min-w-0 leading-tight">
                <div className="font-display font-bold text-white text-base truncate flex items-center">
                  {(user.barns || []).length > 1 ? <BarnSwitcher user={user} barnLabel={barnLabel || "Kingly"} compact /> : (barnLabel || "Kingly")}
                </div>
                <div className="text-[9px] text-amber-300 uppercase tracking-widest font-semibold whitespace-nowrap truncate">
                  Be the King of the Show
                </div>
              </div>
            </div>

            {/* Barn name (desktop only — mobile shows it in the sheet) */}
            <div className="hidden md:flex items-center gap-2.5" data-testid="header-barn">
              {ws?.logo ? (
                <img src={imgSrc(ws.logo)} alt="" className="w-7 h-7 rounded object-contain border border-zinc-200" />
              ) : null}
              <BarnSwitcher user={user} barnLabel={barnLabel} />
              {user.role === "member" && (
                <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 uppercase tracking-wider" data-testid="role-badge">
                  Assistant
                </span>
              )}
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-500 shrink-0">
              <SearchButton />
              <AskKinglyButton />
              <button
                data-testid="barn-mode-toggle"
                onClick={() => setBarnModePref(!isBarnMode())}
                title="Big text (barn mode)"
                className={`px-2 py-1 rounded-md border font-bold leading-none text-[13px] transition-colors ${
                  barnMode
                    ? "bg-amber-400 border-amber-400 text-slate-900"
                    : "border-slate-600 text-slate-300 md:border-zinc-300 md:text-slate-500 hover:opacity-75"
                }`}
              >
                Aa
              </button>
              <LangToggle />
              <PlanPill />
              <span className="w-2 h-2 bg-emerald-500 rounded-full pulse-dot" data-testid="live-dot" />
              <span className="hidden sm:inline">Live · Your workspace</span>
              {user.role === "member" && (
                <span className="md:hidden text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 uppercase tracking-wider" data-testid="role-badge-mobile">
                  Asst
                </span>
              )}
            </div>
          </div>
        </header>

        <PushBanner />
        <PlanLockBanner />
        <AnnouncementBanner className="mx-4 sm:mx-6 md:mx-8 mt-4 max-w-7xl md:mx-auto" />
        {!user?.demo && <DeviceApprovalCard />}
        {!isTrainer && !user.tutorial_done && !tutorialDismissed && (
          <AssistantTutorial onDone={() => setTutorialDismissed(true)} />
        )}

        <div key={location.pathname} ref={pageRef} className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto w-full flex-1 animate-page" data-testid="page-content">
          <Outlet />
        </div>
      </main>

      {/* ===== Mobile bottom tab bar ===== */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/92 backdrop-blur-xl border-t border-zinc-200/80 grid grid-cols-5 h-16 pb-[env(safe-area-inset-bottom)] shadow-[0_-6px_20px_-12px_rgba(15,23,42,0.25)]"
        data-testid="mobile-bottom-nav"
      >
        {MOBILE_PRIMARY.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => recordTap(`nav:${item.to}`)}
            data-testid={item.testid}
            className={({ isActive }) =>
              `relative flex flex-col items-center justify-center gap-1 text-[10px] font-semibold transition-colors active:scale-95 ${
                isActive ? "text-amber-600" : "text-slate-400 active:text-slate-600"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span className={`relative flex items-center justify-center w-12 h-7 rounded-full transition-colors ${isActive ? "bg-amber-100" : ""}`}>
                  <item.icon className={`w-[22px] h-[22px] ${isActive ? "stroke-[2.4]" : "stroke-2"}`} />
                  {item.badge && <UnreadBadge count={unread} className="absolute -top-1 -right-0.5 ring-2 ring-white" />}
                </span>
                <span className={isActive ? "text-slate-900" : ""}>{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="tab-more"
              className="relative flex flex-col items-center justify-center gap-1 text-[10px] font-semibold text-slate-400 active:text-slate-600 active:scale-95 transition-colors"
            >
              <span className="flex items-center justify-center w-12 h-7 rounded-full">
                <MoreHorizontal className="w-[22px] h-[22px] stroke-2" />
              </span>
              <span>More</span>
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[85%] sm:w-80 p-0 flex flex-col">
            <VisuallyHidden>
              <SheetTitle>Navigation</SheetTitle>
              <SheetDescription>Workspace and account menu</SheetDescription>
            </VisuallyHidden>
            <div className="px-5 pt-[calc(env(safe-area-inset-top)+1.25rem)] pb-5 border-b border-zinc-200 bg-gradient-to-b from-white to-zinc-50">
              <div className="flex items-center gap-3">
                <KinglyLogo size={52} />
                <div className="min-w-0">
                  <div className="font-display font-bold text-slate-900 leading-tight text-xl">Kingly</div>
                  <div className="text-[10px] text-amber-700 uppercase tracking-[0.18em] font-semibold mt-0.5">
                    Be the King of the Show
                  </div>
                </div>
              </div>
              {ws && (
                <div className="mt-4 flex items-center gap-2.5">
                  {ws.logo ? (
                    <img src={imgSrc(ws.logo)} alt="" className="w-10 h-10 rounded-md object-contain border border-zinc-200 bg-white" />
                  ) : (
                    <div className="w-10 h-10 rounded-md bg-slate-900 flex items-center justify-center text-xs font-bold text-amber-300 shadow-sm">
                      {(barnLabel || "B").slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{barnLabel}</div>
                    {user.role === "member" && (
                      <div className="text-[10px] uppercase tracking-wider text-amber-700">Assistant</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
              {moreNav.map((item) => item.heading ? (
                <div key={`h-${item.label}`} className="px-3 pt-3 pb-1 text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400" data-testid={`mobile-${item.testid}`}>
                  {item.label}
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={!!item.end}
                  data-testid={`mobile-${item.testid}`}
                  onClick={() => { recordTap(`nav:${item.to}`); setMoreOpen(false); }}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-slate-900 text-white"
                        : "text-slate-700 hover:bg-zinc-100"
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                  {item.feature && <PlanBadge feature={item.feature} className="ml-auto" />}
                  {item.badge && <UnreadBadge count={unread} className="ml-auto" />}
                </NavLink>
              ))}
            </nav>
            <div className="p-3 border-t border-zinc-200">
              <UserPanel user={user} onLogout={logout} />
            </div>
          </SheetContent>
        </Sheet>
      </nav>

      {/* PWA install prompt — mobile only, auto-hides if already installed */}
      <InstallPrompt />
      <AskKinglySheet />
      <GuideOverlay />
    </div>
    </HelpProvider>
  );
}

function UserPanel({ user, onLogout }) {
  return (
    <>
      <div className="flex items-center gap-2 px-3 py-2">
        <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-semibold text-slate-700">
          {user.name?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900 truncate" data-testid="user-name">
            {user.name || user.email}
          </div>
          <div className="text-xs text-slate-500 truncate">{user.email}</div>
        </div>
      </div>
      <Button
        data-testid="logout-button"
        variant="ghost"
        className="w-full justify-start text-slate-600 mt-1 h-10"
        onClick={onLogout}
      >
        <LogOut className="w-4 h-4 mr-2" /> Sign out
      </Button>
    </>
  );
}
